import 'draft-js/dist/Draft.css'
import '@kaidam/fc-shared/assets/texteditor/shared.css'
import '@kaidam/fc-shared/assets/texteditor/editor.css'
import React, { FC, useState, useMemo, useCallback, useEffect, useRef } from 'react'
import makeStyles from '@mui/styles/makeStyles';

import {
  Editor,
  EditorState,
  DraftHandleValue,
  RawDraftContentState,
  convertFromRaw,
  convertToRaw,
} from 'draft-js'

import decorator from './decorators'
import EditorContext from './EditorContext'
import ToolbarButtonMagicTag from './ToolbarButtonMagicTag'

import {
  IData_MagicTag_Type,
} from '@kaidam/fc-shared/src/types/schema/magic_tag'

import {
  blockClassnameFn,
  getColorStyles,
} from './utils'

import {
  ENABLED_MAGIC_TAGS_LINK_EDITOR,
} from '@kaidam/fc-shared/src/constants'

import icons from '../../icons'
const LabelIcon = icons.label

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  buttons: {
    flexGrow: 0,
  },
  editor: {
    flexGrow: 1,
    border: '1px solid #ccc',
    padding: theme.spacing(1),
    cursor: 'text',
    fontSize: '1.3em',
  },
  button: {
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
}))

interface MagicStringEditorProps {
  value?: RawDraftContentState,
  enabled?: IData_MagicTag_Type[],
  onChange: {
    (value: RawDraftContentState): void,
  },
}

const MagicStringEditor: FC<React.PropsWithChildren<MagicStringEditorProps>> = ({
  value,
  enabled = [],
  onChange,
}) => {

  const classes = useStyles()
  const editorRef = useRef<Editor | null>(null)

  const [ editorState, setEditorState ] = useState(() =>
    EditorState.createEmpty(decorator),
  )

  const onChangeHandler = useCallback((state: EditorState) => {
    setEditorState(state)
    onChange(convertToRaw(state.getCurrentContent()))
  }, [])

  // this is e:any because we can't find here SyntheticKeyboardEvents is defined
  const handleReturn = useCallback((e: any, state: EditorState): DraftHandleValue => {
    e.preventDefault()
    e.stopPropagation()
    return 'handled'
  }, [])

  const onFocus = useCallback(() => {
    if(!editorRef.current) return
    editorRef.current.focus()
  }, [
    editorRef.current,
  ])

  const style = useMemo<string>(() => {
    const contentState = editorState.getCurrentContent()
    return getColorStyles(contentState)
  }, [
    editorState,
  ])

  useEffect(() => {
    if(!value) return
    setEditorState(EditorState.createWithContent(convertFromRaw(value), decorator))
  }, [])

  return (
    <EditorContext.Provider value={{
      focus: onFocus,
      disableLinkClicks: true,
    }}>
      <style dangerouslySetInnerHTML={{__html: style}} />
      <div className={ classes.wrapper }>
        <div className={ classes.buttons }>
          <ToolbarButtonMagicTag
            className={ classes.button }
            icon={ LabelIcon as any }
            enabled={ enabled.length > 0 ? enabled : ENABLED_MAGIC_TAGS_LINK_EDITOR }
            addExtraCharacters="/"
            editorState={ editorState }
            setEditorState={ setEditorState }
          />
        </div>
        <div className={ classes.editor }>
          <Editor
            ref={ editorRef }
            editorState={ editorState }
            blockStyleFn={ blockClassnameFn }
            handleReturn={ handleReturn }
            onChange={ onChangeHandler }
          />
        </div>
      </div>
    </EditorContext.Provider>
    
  )
}

export default MagicStringEditor